import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { TicketStatus } from "@sd/types/ticket";
import { statusColors } from "@sd/lib/ticketStreamUtils";
import { DEFAULT_TICKET_STATUS, formatStatusLabel } from "@sd/constants/ticketStatuses";

interface StatusChangeMeta {
  open: boolean;
  ticketId: string | null;
  ticketLabel: string | null;
  newStatus: TicketStatus;
}

export function emptyStatusChange(): StatusChangeMeta {
  return { open: false, ticketId: null, ticketLabel: null, newStatus: DEFAULT_TICKET_STATUS };
}

interface StatusChangeDialogProps {
  state: StatusChangeMeta;
  onClose: () => void;
  onConfirm: (ticketId: string, newStatus: TicketStatus, reason: string) => void;
}

/**
 * Confirm dialog for changing a ticket's status.
 * Manages its own reason text-area state.
 */
export function StatusChangeDialog({ state, onClose, onConfirm }: StatusChangeDialogProps) {
  const [reason, setReason] = useState("");

  const handleConfirm = () => {
    if (!state.ticketId) return;
    onConfirm(state.ticketId, state.newStatus, reason);
    setReason("");
  };

  const handleClose = () => {
    setReason("");
    onClose();
  };

  return (
    <Dialog open={state.open} onOpenChange={o => !o && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm status change</DialogTitle>
          <DialogDescription>
            Update {state.ticketLabel}{" "}
            to{" "}
            <Badge className={cn("mx-1 text-xs", statusColors[state.newStatus])}>
              {formatStatusLabel(state.newStatus)}
            </Badge>
          </DialogDescription>
        </DialogHeader>
        <Textarea
          placeholder="Reason (optional)"
          value={reason}
          onChange={e => setReason(e.target.value)}
        />
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>Confirm</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
